import React, { useCallback, useMemo, useRef, useState } from 'react';

import type { GridApi } from '@mint-lib/ag-grid/community';
import { AiSidebar, useAiSidebarContext } from '@mint-lib/ai-sidebar-circuit';
import { useTranslation } from '@mint-lib/i18n';
import { Close, Information, WarningFilled } from '@mint-lib/icons';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Divider,
  Switch,
  SwitchLabel,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  typographyVariants,
} from '@mint-lib/tw-components';
import { Suggestions } from '@mint-sdk/api-recommendations';

import useSuggestionPermissions from '../../../hooks/useSuggestionPermissions.js';
import { useSyncedSelectedItem } from '../../../hooks/useSyncedSelectedItem.js';
import { useGenerateSuggestion } from '../../../queries/suggestions.js';
import type { SuggestionGridType } from '../../../types/suggestion.js';
import SuggestionProcessingModal from '../../atoms/SuggestionReworkModal/SuggestionReworkModal.jsx';
import { EditDialog, ExitEditDialog } from '../../molecules/Dialogs/index.js';
import SuggestionGrid from '../../molecules/SuggestionGrid/SuggestionGrid.jsx';
import SuggestionHeadline from '../SuggestionHeadline/SuggestionHeadline.jsx';

const ROWS_CONFIG = {
  MEDIA_ROWS_TO_STAY: 2,
  MIN_ROWS_TO_SELECT: 1,
  MIN_ROWS_FOR_EDIT: 3,
} as const;

const detectMulticurrencyProperty = (
  item: Suggestions.TaxonomyChildrenType,
): boolean => {
  if (item.multicurrencyBudgetData) {
    return true;
  }

  if (item.children.length) {
    return item.children.map(detectMulticurrencyProperty).some(Boolean);
  }

  return false;
};

type SuggestionEntityProps = {
  suggestion: Suggestions.SuggestionResponseType;
};

const SuggestionEntity = ({ suggestion }: SuggestionEntityProps) => {
  const { isProcessingAllowed } = useSuggestionPermissions();
  const { t } = useTranslation('@mint-lib/recommendations-circuit');

  const [selectedSuggestion, setSelectedSuggestion] = useState(suggestion);
  const { isAiSidebarOpen } = useAiSidebarContext();

  const [isMultiCurrencyMode, setIsMultiCurrencyMode] =
    useState<boolean>(false);
  const [isErrorAlertShown, setIsErrorAlertShown] = useState<boolean>(true);
  const [isEditingMode, setIsEditingMode] = useState<boolean>(false);
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState<boolean>(false);
  const [isProcessingModalToggled, setIsProcessingModalToggled] =
    useState<boolean>(true);
  const [isProcessingModalOpen, setIsProcessingModalOpen] = useState<boolean>(
    (suggestion.notRead &&
      !!suggestion.originalId &&
      suggestion.status === Suggestions.Statuses.ACTIVE) || // Reworked, but not read
      suggestion.status === Suggestions.Statuses.REWORKING,
  );

  const [isGenerateModalOpen, setIsGenerateModalOpen] =
    useState<boolean>(false);

  const [mediaRows, setMediaRows] = useState<{
    selectedMediaRowIds: number[];
    mediaRowIds: number[];
  }>({
    selectedMediaRowIds: [],
    mediaRowIds: [],
  });
  const gridApiRef = useRef<GridApi<SuggestionGridType>>(null);

  const {
    mutate: generateSuggestion,
    isSuccess: isGenerateSuccess,
    isError: isGenerateError,
    isPending: isGeneratePending,
    error: generateError,
  } = useGenerateSuggestion();

  const isGenerateBtnDisabled = useMemo(() => {
    if (
      isGenerateSuccess ||
      isGeneratePending ||
      mediaRows.selectedMediaRowIds.length < ROWS_CONFIG.MIN_ROWS_TO_SELECT
    ) {
      return true;
    }

    if (mediaRows.selectedMediaRowIds.length) {
      const availableRows = mediaRows.mediaRowIds.filter(
        (id) => !mediaRows.selectedMediaRowIds.includes(id),
      );

      return availableRows.length < ROWS_CONFIG.MEDIA_ROWS_TO_STAY;
    }
    return false;
  }, [mediaRows, isGenerateSuccess, isGeneratePending]);

  useSyncedSelectedItem();

  const isSuggWithError =
    selectedSuggestion.status === Suggestions.Statuses.ERROR;

  const hasSelectedItems = mediaRows.selectedMediaRowIds.length > 0;

  const shouldShowEditButton = useMemo(
    () =>
      selectedSuggestion.status === Suggestions.Statuses.ACTIVE &&
      !selectedSuggestion.originalId && // !isReworked
      selectedSuggestion.enabled && // isPublished
      !selectedSuggestion.reworkDate && // !isReworking
      isProcessingAllowed,
    [selectedSuggestion, isProcessingAllowed],
  );
  const onClickCurrencyToggler = () => {
    setIsMultiCurrencyMode((prev) => !prev);
  };

  const getErrorDescription = () => {
    if (selectedSuggestion.platformStatuses.length) {
      const { platformStatuses } = selectedSuggestion;

      const allPlatformErrors = platformStatuses.flatMap((plat) =>
        plat.data.filter((el) => el.status === 'error'),
      );

      //FYI: According this ticket https://mint-ai.atlassian.net/browse/PDT-66023
      // Suggestion can have more than 1 error in platformStatus obj, but our application is able to display 1 at the moment
      // So we decide display 'Media plan is locked' because this msg has the highest priority. Together with the team, we decided to make this a temporary solution
      const specialError = allPlatformErrors.find((el) =>
        el.details?.includes('Media plan is locked'),
      );

      return (specialError || allPlatformErrors[0]).details;
    }

    return t('There was an error with this suggestion.');
  };

  const onCloseErrorAlert = () => {
    setIsErrorAlertShown(false);
  };

  const handleLeaveModalOpen = () => {
    if (hasSelectedItems) {
      setIsLeaveModalOpen(true);
      return;
    }

    gridApiRef.current?.deselectAll();
    setIsEditingMode(false);
  };

  const handleModalClose = () => {
    setIsLeaveModalOpen(false);
  };

  const handleCloseGenerateModal = () => {
    setIsGenerateModalOpen(false);
  };

  const handleLeaveEditMode = () => {
    gridApiRef.current?.deselectAll();
    setIsEditingMode(false);
    setIsLeaveModalOpen(false);
    setMediaRows((prev) => ({
      selectedMediaRowIds: [],
      mediaRowIds: prev.mediaRowIds,
    }));
  };

  const handleToggleProcessingModal = () =>
    setIsProcessingModalToggled((prevState) => !prevState);

  const handleProcessingModalClose = () => {
    setIsProcessingModalOpen(false);
  };

  const handleDeleteTaxonomyItems = useCallback(() => {
    const rowsToDelete = mediaRows.selectedMediaRowIds;
    setIsProcessingModalOpen(true);
    generateSuggestion(
      {
        suggestionId: +selectedSuggestion.id,
        mediaRowIds: mediaRows.mediaRowIds.filter(
          (id) => !rowsToDelete.includes(id),
        ),
        mediaPlanId: selectedSuggestion.mediaPlanId,
      },
      {
        onSuccess: () => {
          gridApiRef.current?.deselectAll();
          setMediaRows((prev) => ({
            selectedMediaRowIds: [],
            mediaRowIds: prev.mediaRowIds,
          }));
        },
      },
    );
    setIsLeaveModalOpen(false);
    setIsEditingMode(false);
    handleCloseGenerateModal();
  }, [generateSuggestion, mediaRows, selectedSuggestion]);

  const isMulticurrencyModeAvailable = selectedSuggestion.taxonomy.children
    .map(detectMulticurrencyProperty)
    .some(Boolean);

  const isEditBtnDisabled =
    mediaRows.mediaRowIds.length < ROWS_CONFIG.MIN_ROWS_FOR_EDIT ||
    isGenerateSuccess ||
    isGeneratePending;

  return (
    <div className="flex h-full max-h-full w-full [scrollbar-width:none] flex-col overflow-auto">
      <div className={'flex h-full w-full flex-col'}>
        <SuggestionHeadline
          isGenerating={isGenerateSuccess || isGeneratePending}
          suggestion={selectedSuggestion}
          isEditingMode={isEditingMode}
          updateSuggestion={setSelectedSuggestion}
          closeGenerationModal={handleProcessingModalClose}
        />
        <div className="mt-4 flex h-full flex-col gap-4">
          {isSuggWithError && isErrorAlertShown && (
            <Alert
              severity="error"
              variant="inline"
              className="flex flex-row items-center justify-between"
            >
              <div className="flex flex-row items-center gap-1">
                <AlertTitle className="flex flex-row items-center justify-center gap-2">
                  <WarningFilled />
                  {t('Error')}
                </AlertTitle>
                <AlertDescription>{getErrorDescription()}</AlertDescription>
              </div>
              <Button
                variant="plain"
                className="flex cursor-pointer flex-row items-center p-1"
                onClick={onCloseErrorAlert}
              >
                <Close />
              </Button>
            </Alert>
          )}
          <div className="flex flex-row justify-between">
            <p className={typographyVariants({ variant: 'h4' })}>
              {t('Explore data')}
            </p>

            <div className="flex flex-row items-center gap-2">
              <>
                {isEditingMode ? (
                  <>
                    <Button
                      variant="text"
                      color="primary"
                      size="small"
                      aria-label="Exit editing mode"
                      onClick={handleLeaveModalOpen}
                      disabled={isGenerateSuccess || isGeneratePending}
                    >
                      {t('Cancel')}
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      size="small"
                      aria-label="Delete selected items"
                      disabled={isGenerateBtnDisabled}
                      onClick={() => setIsGenerateModalOpen(true)}
                    >
                      {t('Delete selected items')}
                    </Button>
                  </>
                ) : (
                  shouldShowEditButton && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={isEditBtnDisabled}
                          aria-label="Edit suggestion"
                          onClick={() => setIsEditingMode(true)}
                          className="flex flex-row gap-1"
                        >
                          <Information />
                          {t('Edit')}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent
                        sideOffset={10}
                        side="top"
                        align="center"
                        arrow={true}
                      >
                        {t(
                          'Edit to exclude platforms or media rows from suggestion.',
                        )}
                      </TooltipContent>
                    </Tooltip>
                  )
                )}
                <Divider
                  orientation="vertical"
                  className="mx-2 bg-border-secondary"
                />
              </>

              <SwitchLabel htmlFor="local-currency">
                <Switch
                  aria-label="controlled"
                  id="local-currency"
                  checked={isMultiCurrencyMode}
                  disabled={!isMulticurrencyModeAvailable}
                  size="small"
                  onClick={onClickCurrencyToggler}
                />
                <p className={typographyVariants({ variant: 'bodyShort01' })}>
                  {t('Local currency')}
                </p>
              </SwitchLabel>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Information className="size-4 fill-icon-primary" />
                </TooltipTrigger>
                <TooltipContent
                  sideOffset={10}
                  side="top"
                  align="center"
                  arrow={true}
                >
                  {isMulticurrencyModeAvailable
                    ? t('Switch from campaign to local currencies')
                    : t(
                        "It's not possible to convert currency to mediarow currency",
                      )}
                </TooltipContent>
              </Tooltip>
            </div>
          </div>

          <SuggestionGrid
            key={selectedSuggestion.id}
            suggestion={selectedSuggestion}
            isMultiCurrencyMode={isMultiCurrencyMode}
            isEditingMode={isEditingMode}
            setMediaRows={setMediaRows}
            gridApiRef={gridApiRef}
          />

          <EditDialog
            handleGenerate={handleDeleteTaxonomyItems}
            onClose={handleCloseGenerateModal}
            isOpen={isGenerateModalOpen}
          />
          <ExitEditDialog
            isOpen={isLeaveModalOpen}
            onClose={handleModalClose}
            handleLeave={handleLeaveEditMode}
          />
        </div>
      </div>
      {isAiSidebarOpen && <AiSidebar />}
      {isProcessingModalOpen && (
        <SuggestionProcessingModal
          isDrawerOpen={isAiSidebarOpen}
          mode="generate"
          isError={isGenerateError}
          error={generateError}
          isSuccess={!!suggestion.originalId}
          onExpandButtonClick={handleToggleProcessingModal}
          onCloseButtonClick={handleProcessingModalClose}
          isModalExpanded={isProcessingModalToggled}
        />
      )}
    </div>
  );
};

export default SuggestionEntity;
